/**
 * 認証関連のTanStack Query v5 hooks
 * UNIFIED_DEVELOPMENT_GUIDE準拠
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAuth as useAuthContext } from '../contexts/AuthContext';
import axios from '@/lib/axios';
import type { 
  LoginFormValues, 
  NFCLoginValues, 
  ForgotPasswordValues, 
  ResetPasswordValues,
  ChangeStoreValues 
} from '../schema';

// Query Keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  verify: () => [...authKeys.all, 'verify'] as const,
};

// API Version設定（環境変数またはデフォルトでv3）
const API_VERSION = import.meta.env.VITE_API_VERSION ?? 'v3';

// API Functions
const authApi = {
  // CSRF取得
  csrf: () => axios.get('/sanctum/csrf-cookie'),

  // ログイン
  login: async (data: LoginFormValues) => {
    await authApi.csrf();
    // remember → remember_me に変換（バックエンドの期待するフィールド名）
    const requestData = {
      ...data,
      remember_me: data.remember,
    };
    delete (requestData as any).remember;
    const response = await axios.post(`/api/${API_VERSION}/auth/login`, requestData);
    return response.data;
  },

  // NFCログイン
  nfcLogin: async (data: NFCLoginValues) => {
    await authApi.csrf();
    const response = await axios.post(`/api/${API_VERSION}/auth/nfc-login`, data);
    return response.data;
  },

  // 現在のユーザー取得
  getUser: async () => {
    const response = await axios.get(`/api/${API_VERSION}/auth/user`);
    return response.data;
  },

  // 認証確認
  verify: async () => {
    const response = await axios.get(`/api/${API_VERSION}/auth/verify`);
    return response.data;
  },

  // ログアウト
  logout: async () => {
    const response = await axios.post(`/api/${API_VERSION}/auth/logout`);
    return response.data;
  },

  // パスワードリセット要求
  forgotPassword: async (data: ForgotPasswordValues) => {
    const response = await axios.post(`/api/${API_VERSION}/auth/forgot-password`, data);
    return response.data;
  },

  // パスワードリセット
  resetPassword: async (data: ResetPasswordValues) => {
    const response = await axios.post(`/api/${API_VERSION}/auth/reset-password`, data);
    return response.data;
  },

  // 店舗切り替え
  changeStore: async (data: ChangeStoreValues) => {
    const response = await axios.post(`/api/${API_VERSION}/auth/change-store`, data);
    return response.data;
  },
};

/**
 * ログイン用のmutation hook
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const authContext = useAuthContext();
  
  return useMutation({
    mutationFn: async (data: LoginFormValues) => {
      // AuthContextのlogin関数を使用（エラーハンドリングも含む）
      await authContext.login(data.email, data.password);
      // リダイレクト
      navigate('/dashboard');
    },
  });
};

/**
 * NFCログイン用のmutation hook
 */
export const useNFCLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.nfcLogin,
    onSuccess: (data) => {
      // キャッシュ更新
      queryClient.setQueryData(authKeys.user(), data.user);
      queryClient.invalidateQueries({ queryKey: authKeys.all });
      
      // 成功通知
      notifications.show({
        color: 'green',
        title: 'NFCログインしました',
        message: `${data.user?.name || 'ユーザー'}さん、おかえりなさい`,
      });
      
      // リダイレクト
      navigate('/dashboard');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'NFCログインに失敗しました';
      notifications.show({
        color: 'red',
        title: 'エラー',
        message,
      });
    },
  });
};

/**
 * 現在のユーザー取得用のquery hook
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: authApi.getUser,
    staleTime: 1000 * 60 * 5, // 5分
    gcTime: 1000 * 60 * 10, // 10分
    retry: 1,
  });
};

/**
 * 認証確認用のquery hook
 */
export const useVerifyAuth = () => {
  return useQuery({
    queryKey: authKeys.verify(),
    queryFn: authApi.verify,
    staleTime: 1000 * 60 * 5, // 5分
    gcTime: 1000 * 60 * 10, // 10分
    retry: false,
  });
};

/**
 * ログアウト用のmutation hook
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // キャッシュクリア
      queryClient.clear();
      
      // 成功通知
      notifications.show({
        color: 'blue',
        title: 'ログアウトしました',
        message: 'またのご利用をお待ちしております',
      });
      
      // ログインページへリダイレクト
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'ログアウトに失敗しました';
      notifications.show({
        color: 'red',
        title: 'エラー',
        message,
      });
    },
  });
};

/**
 * パスワードリセット要求用のmutation hook
 */
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => {
      notifications.show({
        color: 'green',
        title: '送信しました',
        message: 'パスワードリセットのリンクをメールで送信しました',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'メール送信に失敗しました';
      notifications.show({
        color: 'red',
        title: 'エラー',
        message,
      });
    },
  });
};

/**
 * パスワードリセット用のmutation hook
 */
export const useResetPassword = () => {
  const navigate = useNavigate();
  
  return useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      notifications.show({
        color: 'green',
        title: 'パスワードをリセットしました',
        message: '新しいパスワードでログインしてください',
      });
      navigate('/login');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'パスワードリセットに失敗しました';
      notifications.show({
        color: 'red',
        title: 'エラー',
        message,
      });
    },
  });
};

/**
 * 店舗切り替え用のmutation hook
 */
export const useChangeStore = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.changeStore,
    onSuccess: (data) => {
      // ユーザー情報更新
      queryClient.setQueryData(authKeys.user(), data.user);
      queryClient.invalidateQueries({ queryKey: authKeys.all });
      
      notifications.show({
        color: 'green',
        title: '店舗を切り替えました',
        message: `${data.store?.name || '店舗'}に切り替えました`,
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || '店舗切り替えに失敗しました';
      notifications.show({
        color: 'red',
        title: 'エラー',
        message,
      });
    },
  });
};